import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Shield, Mail, Lock, ArrowLeft, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API}/auth/local/forgot-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!r.ok) { const d = await r.json().catch(()=>({})); throw new Error(d.detail || 'Greška'); }
      setSubmitted(true);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 text-gray-100 p-4" data-testid="forgot-pwd-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <Shield size={28} className="text-red-500" /><h1 className="text-2xl font-bold">E-MINA Admin</h1>
        </div>
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold mb-1 flex items-center gap-2"><Mail size={16}/> Resetiranje lozinke</h2>
          <p className="text-xs text-slate-400 mb-5">Unesite e-mail — poslat ćemo vam link za postavljanje nove lozinke (vrijedi 30 min).</p>
          {submitted ? (
            <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 p-4 text-sm flex items-start gap-3" data-testid="forgot-pwd-success">
              <Check size={18} className="text-emerald-400 shrink-0 mt-0.5"/>
              <div>
                <p className="font-semibold text-emerald-200">Ako e-mail postoji u sustavu, link je poslan.</p>
                <p className="text-slate-400 mt-1 text-xs">Provjerite pristiglu poštu (i spam). Ako SendGrid nije konfiguriran, link je zapisan u backend logovima.</p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="block text-xs text-slate-400 mb-1">E-mail</label>
                <input type="email" required value={email} onChange={e=>setEmail(e.target.value)}
                       className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2.5 text-sm focus:border-red-400 outline-none"
                       data-testid="forgot-pwd-email"/>
              </div>
              {error && <p className="text-xs text-red-400">{error}</p>}
              <button type="submit" disabled={loading||!email}
                      className="w-full py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-md text-sm disabled:opacity-50"
                      data-testid="forgot-pwd-submit">
                {loading ? 'Šaljem…' : 'Pošalji link za reset'}
              </button>
            </form>
          )}
          <Link to="/admin/login" className="mt-5 inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-red-400">
            <ArrowLeft size={12}/> Natrag na prijavu
          </Link>
        </div>
      </div>
    </div>
  );
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const token = sp.get('token') || '';
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (pw !== pw2) { setError('Lozinke se ne poklapaju'); return; }
    if (pw.length < 8) { setError('Minimum 8 znakova'); return; }
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API}/auth/local/reset-password`, {
        method: 'POST', headers: { 'Content-Type':'application/json' },
        body: JSON.stringify({ token, new_password: pw }),
      });
      if (!r.ok) { const d = await r.json().catch(()=>({})); throw new Error(d.detail || 'Greška'); }
      setSuccess(true);
      setTimeout(() => navigate('/admin/login'), 1600);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 text-gray-100 p-4" data-testid="reset-pwd-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <Shield size={28} className="text-red-500" /><h1 className="text-2xl font-bold">E-MINA Admin</h1>
        </div>
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold mb-1 flex items-center gap-2"><Lock size={16}/> Nova lozinka</h2>
          <p className="text-xs text-slate-400 mb-5">Postavite novu lozinku (minimum 8 znakova).</p>
          {!token && <p className="text-xs text-red-400 mb-3" data-testid="reset-pwd-no-token">Nedostaje token u URL-u. Otvorite link iz e-maila.</p>}
          {success ? (
            <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 p-4 text-sm flex items-start gap-3" data-testid="reset-pwd-success">
              <Check size={18} className="text-emerald-400 shrink-0 mt-0.5"/>
              <div><p className="font-semibold text-emerald-200">Lozinka je promijenjena!</p>
                <p className="text-slate-400 mt-1 text-xs">Preusmjeravam na prijavu…</p></div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Nova lozinka</label>
                <input type="password" required minLength={8} value={pw} onChange={e=>setPw(e.target.value)}
                       data-testid="reset-pwd-new"
                       className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2.5 text-sm focus:border-red-400 outline-none"/>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Potvrdi lozinku</label>
                <input type="password" required minLength={8} value={pw2} onChange={e=>setPw2(e.target.value)}
                       data-testid="reset-pwd-confirm"
                       className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2.5 text-sm focus:border-red-400 outline-none"/>
              </div>
              {error && <p className="text-xs text-red-400" data-testid="reset-pwd-error">{error}</p>}
              <button type="submit" disabled={loading||!token}
                      className="w-full py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-md text-sm disabled:opacity-50"
                      data-testid="reset-pwd-submit">
                {loading ? 'Spremam…' : 'Postavi novu lozinku'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
